import { useMemo } from "react";
import { GalleryImage, GalleryImageSource } from "./ImageGalleryTypes";

// Helper to detect image file extensions
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i;
const isImageFile = (name: string): boolean => IMAGE_EXTENSIONS.test(name);
const isBase64Image = (content: string): boolean =>
  content.startsWith("data:image/");

/**
 * Normalizes different data source formats into a uniform GalleryImage[].
 *
 * Supported source types:
 * - fileModels: FileModel[] from shared models (filter to images only)
 * - entityFiles: ServiceRequestFile / OfferFile arrays (with size)
 * - urls: Plain string URL array
 * - machine: Machine entity with optional image/catalogImageUrl/files fallback chain
 */
export function useGalleryImages(source: GalleryImageSource): GalleryImage[] {
  return useMemo(() => {
    switch (source.type) {
      case "fileModels": {
        if (!source.files || source.files.length === 0) return [];
        return source.files
          .filter((f) => {
            if (!f.content) return false;
            if (isBase64Image(f.content)) return true;
            if (f.name && isImageFile(f.name)) return true;
            return false;
          })
          .map((f, index) => ({
            id: `file-${f.name || index}-${index}`,
            src: f.content!,
            alt: f.name || `Image ${index + 1}`,
            name: f.name,
          }));
      }

      case "entityFiles": {
        if (!source.files || source.files.length === 0) return [];
        return source.files
          .filter((f) => {
            if (!f.content) return false;
            if (isBase64Image(f.content)) return true;
            if (f.name && isImageFile(f.name)) return true;
            return false;
          })
          .map((f, index) => ({
            id: `entity-${f.name}-${index}`,
            src: f.content,
            alt: f.name || `Image ${index + 1}`,
            name: f.name,
            size: f.size,
          }));
      }

      case "urls": {
        if (!source.urls || source.urls.length === 0) return [];
        return source.urls
          .filter((url) => !!url)
          .map((url, index) => ({
            id: `url-${index}`,
            src: url,
            alt: `Image ${index + 1}`,
          }));
      }

      case "machine": {
        const images: GalleryImage[] = [];

        // Priority 1: Files array (uploaded machine photos)
        if (source.files && source.files.length > 0) {
          source.files
            .filter((f) => {
              if (!f.content) return false;
              if (isBase64Image(f.content)) return true;
              if (f.name && isImageFile(f.name)) return true;
              return false;
            })
            .forEach((f, index) => {
              images.push({
                id: `machine-file-${f.name}-${index}`,
                src: f.content,
                alt: f.name || `Machine image ${index + 1}`,
                name: f.name,
              });
            });
        }

        // Priority 2: Single image field (legacy base64 or URL)
        if (images.length === 0 && source.image) {
          images.push({
            id: "machine-image",
            src: source.image,
            alt: "Machine image",
          });
        }

        // Priority 3: Catalog imagery (only when the machine carries no own photo).
        //
        // The catalog model ROW (CatalogMachineModels.ImagePath, surfaced as
        // catalogImageUrl) is the AUTHORITATIVE hero. Every other consumer of a
        // catalog image reads that column and never CatalogModelImages: the
        // catalog browse cards, MachinesService, NameplateController and
        // PublicMachinesController all build their URL from CatalogModel.ImagePath
        // via CatalogImageUrlBuilder. Catalog image curation also updates that
        // column.
        //
        // CatalogModelImages holds a denormalised COPY of that hero
        // (imageType === "hero", displayOrder 0) plus the genuine extra
        // angle/detail shots. When curation repoints the model row without
        // rewriting the copy, the two disagree — and because this hook preferred
        // the copy, detail surfaces rendered the stale image while cards rendered
        // the corrected one.
        //
        // So: take the hero from the model row, and take ONLY the non-hero rows
        // from CatalogModelImages. Measured against all 6,046 catalog models via
        // the live API (2026-08-04): 4,175 carry image rows, each with exactly one
        // hero row; the hero row matches the model row for 4,173 of them, so this
        // is a no-op there and corrects the 2 that disagree (TOSHULIN POWERTURN
        // 3000 -> kiwa/kh-45, POWERTURN 4000 -> knuth/bo-90). No model has a hero
        // row without a model-row image, so nothing loses an image; the fallback
        // below still consumes every row when the model row is empty.
        if (images.length === 0) {
          if (source.catalogImageUrl) {
            images.push({
              id: "machine-catalog",
              src: source.catalogImageUrl,
              alt: "Catalog image",
              isPrimary: true,
            });
          }

          (source.catalogImages || []).forEach((img, index) => {
            // Skip the denormalised hero duplicate once we already have the
            // authoritative hero from the model row.
            if (source.catalogImageUrl && img.imageType === "hero") return;
            if (images.some((existing) => existing.src === img.imageStandardUrl)) return;
            images.push({
              id: `catalog-img-${img.id}`,
              src: img.imageStandardUrl,
              alt: `Catalog ${img.imageType || "image"} ${index + 1}`,
              isPrimary: img.isPrimary,
            });
          });
        }

        return images;
      }

      default:
        return [];
    }
  }, [source]);
}
